import { useCallback, useEffect, useRef, useState } from "react";
import type { ResponsePatch } from "./types";

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";
export const RETRY_BACKOFF_MS = [2000, 4000, 8000] as const;

function merge(a: ResponsePatch, b: ResponsePatch): ResponsePatch {
  const out: ResponsePatch = {};
  if (a.answers || b.answers) out.answers = { ...a.answers, ...b.answers };
  if (a.section_progress || b.section_progress) out.section_progress = { ...a.section_progress, ...b.section_progress };
  return out;
}

function isEmpty(p: ResponsePatch): boolean {
  return !p.answers && !p.section_progress;
}

/**
 * Debounced autosave. Changes are merged into one pending patch and sent `delay` ms after the
 * last change. A failed batch is merged back (newer edits win) and retried after 2 s, 4 s, 8 s;
 * after that the status stays `error` until `retryNow()`.
 */
export function useAutosave(onPatch: (patch: ResponsePatch) => Promise<void>, delay = 800) {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [retrying, setRetrying] = useState(false);
  const pending = useRef<ResponsePatch>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(false);
  const failures = useRef(0);
  const onPatchRef = useRef(onPatch);
  onPatchRef.current = onPatch;
  const mounted = useRef(true);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const flush = useCallback(async (): Promise<void> => {
    clear();
    if (inFlight.current || isEmpty(pending.current)) return;
    const batch = pending.current;
    pending.current = {};
    inFlight.current = true;
    if (mounted.current) setStatus("saving");
    try {
      await onPatchRef.current(batch);
      failures.current = 0;
      inFlight.current = false;
      if (!mounted.current) return;
      setRetrying(false);
      if (isEmpty(pending.current)) setStatus("saved");
      else schedule(delay);
    } catch {
      inFlight.current = false;
      pending.current = merge(batch, pending.current);
      failures.current += 1;
      if (!mounted.current) return;
      setStatus("error");
      const wait = RETRY_BACKOFF_MS[failures.current - 1];
      if (wait !== undefined) {
        setRetrying(true);
        schedule(wait);
      } else {
        setRetrying(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay]);

  function schedule(ms: number) {
    clear();
    timer.current = setTimeout(() => void flush(), ms);
  }

  const queue = useCallback(
    (patch: ResponsePatch) => {
      pending.current = merge(pending.current, patch);
      // While waiting out a retry backoff, keep the backoff instead of hammering the server.
      if (failures.current > 0 && timer.current) return;
      if (failures.current > RETRY_BACKOFF_MS.length) return;
      schedule(delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay],
  );

  const retryNow = useCallback(() => {
    failures.current = 0;
    setRetrying(false);
    void flush();
  }, [flush]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Best effort: send anything still pending when the form goes away.
      if (!isEmpty(pending.current) && !inFlight.current) {
        clear();
        const batch = pending.current;
        pending.current = {};
        void onPatchRef.current(batch).catch(() => undefined);
      }
    };
  }, []);

  return { status, retrying, queue, flush, retryNow };
}
